import React, { FC, useCallback, useMemo, useState } from 'react';
import { Avatar, ButtonBase, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { sortBy } from 'lodash';
import { useProjectState } from '@client/store/project';
import { useUser, useUserLookup, useUsers } from '@client/hooks/use-user.hooks';
import { isAdmin } from '@shared/helpers/user.helpers';
import { IProjectInfo } from '@shared/models/project.model';
import UserAvatar from '@features/users/UserAvatar';
import { Menu } from '@ui/menus/Menu';
import { OPEN_AI_USER_ID } from '@shared/constants';
import { useOpenPaywallDialog } from './Paywall/paywall.hook';

type ProjectOwnerMenuProps = {
  projectInfo: IProjectInfo;
  onClose?: () => void;
};

/** Matches the project-list owner avatar size. */
const OWNER_AVATAR_SIZE = 18;

const useStyles = makeStyles()(() => ({
  trigger: {
    width: OWNER_AVATAR_SIZE,
    height: OWNER_AVATAR_SIZE,
    flexShrink: 0,
    borderRadius: '50%',
  },
  placeholderAvatar: {
    width: OWNER_AVATAR_SIZE,
    height: OWNER_AVATAR_SIZE,
    fontSize: 11,
    backgroundColor: 'rgba(0,0,0,0.15)',
    color: 'rgba(0,0,0,0.4)',
  },
}));

/**
 * Owner control on a project-list row.
 * A missing or unknown owner still renders a placeholder so a new owner can be chosen.
 */
const ProjectOwnerMenu: FC<ProjectOwnerMenuProps> = ({
  projectInfo,
  onClose,
}) => {
  const { classes } = useStyles();
  const { updateProjectDetails } = useProjectState('updateProjectDetails');
  const users = useUsers();
  const usersLookup = useUserLookup();
  const loggedInUser = useUser();
  const { openPaywallDialog, isBehindPaywall } = useOpenPaywallDialog();
  const sortedUsers = useMemo(
    () =>
      sortBy(
        users.filter(
          (user) =>
            user.id !== OPEN_AI_USER_ID &&
            (isAdmin(loggedInUser) || !isAdmin(user)),
        ),
        (user) => user.name.toLowerCase(),
      ),
    [users, loggedInUser],
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);
  const openMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (isBehindPaywall('change-project-owner')) {
        openPaywallDialog('change-project-owner');
        return;
      }
      setAnchorEl(event.currentTarget);
    },
    [isBehindPaywall, openPaywallDialog],
  );
  const changeOwner = useCallback(
    (owner: string) => {
      updateProjectDetails({
        reference: Number(projectInfo.id),
        data: { owner },
      });
    },
    [projectInfo.id, updateProjectDetails],
  );
  const ownerUser = usersLookup[projectInfo.owner];

  return (
    <>
      <Tooltip title={ownerUser?.name ?? 'Set project owner'}>
        <ButtonBase
          aria-label={
            ownerUser
              ? `Change project owner: ${ownerUser.name}`
              : 'Set project owner'
          }
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          className={classes.trigger}
          disabled={!sortedUsers.length}
          onClick={openMenu}
        >
          {ownerUser ? (
            <UserAvatar user={ownerUser} size={OWNER_AVATAR_SIZE} />
          ) : (
            // Empty and unknown owners still need a click target so an owner can be assigned.
            <Avatar className={classes.placeholderAvatar}>?</Avatar>
          )}
        </ButtonBase>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {sortedUsers.map((user) => (
          <Menu.Item
            key={user.id}
            selected={user.id === projectInfo.owner}
            icon={<UserAvatar user={user} size={OWNER_AVATAR_SIZE} />}
            onClick={() => changeOwner(user.id)}
          >
            {user.name}
          </Menu.Item>
        ))}
      </Menu>
    </>
  );
};

export default ProjectOwnerMenu;
